import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { PropTypes } from "prop-types";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { computeDropOff } from "../../services/funnelReport.js";

// "Booked" (was "Completed") covers both front-ends: the Next.js site bumps the
// step to >= 4, the widget gets a `bookedDate` stamp from the webhook. The
// backend funnel endpoint reconciles both into the `completed` bucket.
const STEPS = [
  { key: "step0", label: "Entered" },
  { key: "step0_5", label: "Contact" },
  { key: "step1", label: "Info done" },
  { key: "step2", label: "Slot picked" },
  { key: "step3", label: "Reviewed" },
  { key: "completed", label: "Booked" },
];

const STEP_COLORS = [
  "#cdb6df",
  "#c2a3dc",
  "#c98fc0",
  "#cc77a6",
  "#c2608e",
  "#198754",
];

function FunnelChart({ counts, loading, error }) {
  const { labels, values, dropOffs } = useMemo(() => {
    const vals = STEPS.map((s) => counts?.[s.key] ?? 0);
    const drops = STEPS.map((s, i) =>
      i === 0 ? null : computeDropOff(vals[i - 1], vals[i])
    );
    const lbls = STEPS.map((s) => s.label);
    return { labels: lbls, values: vals, dropOffs: drops };
  }, [counts]);

  const data = {
    labels,
    datasets: [
      {
        label: "Count",
        data: values,
        backgroundColor: STEP_COLORS,
        borderRadius: 6,
        borderSkipped: false,
        maxBarThickness: 38,
      },
    ],
  };

  const options = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 16 } },
    plugins: {
      title: { display: false },
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const d = dropOffs[ctx.dataIndex];
            return d == null
              ? `Count: ${ctx.parsed.x}`
              : `Count: ${ctx.parsed.x} • Drop-off from previous: ${d.toFixed(1)}%`;
          },
        },
      },
    },
    scales: {
      x: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: "#f1edf3" } },
      y: { grid: { display: false }, ticks: { font: { size: 13 } } },
    },
  };

  if (loading) {
    return (
      <div className="lb-chart-state">
        <FontAwesomeIcon spin icon={faSpinner} style={{ fontSize: 22 }} />
        <span>Loading funnel…</span>
      </div>
    );
  }

  if (error) {
    return <div className="lb-chart-state">Couldn’t load the funnel: {error}</div>;
  }

  if (!counts || !counts.step0) {
    return <div className="lb-chart-state">No bookings started for this date.</div>;
  }

  return (
    <div>
      <div className="lb-chart lb-chart--funnel">
        <Bar options={options} data={data} />
      </div>
    </div>
  );
}

FunnelChart.propTypes = {
  counts: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

export default FunnelChart;
